import React from 'react';
import { SpaceBetween, Alert, Button, Spinner, Modal, Icon, Box } from '@amzn/awsui-components-core';
import { DocumentUploader } from '../../components/upload/DocumentUploader';
import { EAHuddleService } from 'src/services/EAHuddleService';

/** Sentinel error code from create_meeting_handler.py when file lacks meeting context */
const INSUFFICIENT_MEETING_DETAILS = 'INSUFFICIENT_MEETING_DETAILS';

interface UploadTemplateContainerProps {
  visible: boolean;
  onClose: () => void;
  isProcessingDocument: boolean;
  documentError: string;
  onFileSelected: (file: File) => void;
  onFormattedContentExtracted: (htmlContent: string) => void;
}

export const UploadTemplateContainer: React.FC<UploadTemplateContainerProps> = ({
  visible,
  onClose,
  isProcessingDocument,
  documentError,
  onFileSelected,
  onFormattedContentExtracted
}) => {
  const handleFileSelected = (file: File) => {
    onFileSelected(file);
  };

  return (
    <Modal visible={visible} onDismiss={onClose} header="Smart Upload" size="medium">
      <SpaceBetween direction="vertical" size="m">
        <Box color="text-body-secondary" fontSize="body-m">
          Upload a file and Huddle+ will automatically extract attendees and meeting details using AI.
        </Box>

        <DocumentUploader
          onFileSelected={handleFileSelected}
          onFormattedContentExtracted={onFormattedContentExtracted}
          isProcessing={isProcessingDocument}
          error={documentError === INSUFFICIENT_MEETING_DETAILS ? undefined : documentError}
          shouldRenderPreview={true}
        />

        <Button variant="inline-link" iconName="download" onClick={() => EAHuddleService.downloadTemplate()}>
          Download sample template
        </Button>

        {isProcessingDocument && (
          <Alert statusIconAriaLabel="Loading" type="info">
            <SpaceBetween direction="horizontal" size="s" alignItems="center">
              <Spinner size="normal" />
              <span>Analyzing document and resolving attendees — this may take up to 30 seconds.</span>
            </SpaceBetween>
          </Alert>
        )}

        {documentError && (
          <Alert
            statusIconAriaLabel="Error"
            type="error"
            header={documentError === INSUFFICIENT_MEETING_DETAILS ? "Couldn't extract meeting details" : 'Upload failed'}
          >
            {documentError === INSUFFICIENT_MEETING_DETAILS ? (
              <span>The file doesn't contain enough meeting context. Add a title, attendees, or agenda and try again.</span>
            ) : (
              <span>
                Please try uploading again. If the issue persists,{' '}
                <a href="https://t.corp.amazon.com/create/templates/8d963482-2c95-4b8f-bdbd-b9dbc1db0bc0" target="_blank" rel="noopener noreferrer">
                  file a ticket
                </a>
                .
              </span>
            )}
          </Alert>
        )}
      </SpaceBetween>
    </Modal>
  );
};
